import { useEffect, useState } from 'react';
import { useAuth } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { Select } from '../../../../components/inputs/Select';
import { Stepper } from '../../../../components/Stepper';
import { TextField } from '../../../../components/inputs/TextField';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { eligibilitiesApi } from '../../eligibilities/eligibilitiesApi';
import type { EligibilityOption } from '../../eligibilities/eligibilityTypes';
import { orgUnitsApi } from '../../organization/organizationApi';
import { PositionFields } from '../../positions/components/PositionFields';
import { positionsApi } from '../../positions/positionsApi';
import type { PositionOption } from '../../positions/positionTypes';
import {
  gradeOptionsFor,
  positionDraftFrom,
  positionDraftGrade,
  positionDraftValid,
  positionFieldIds,
  positionRequestFrom,
  qualificationRowsFrom,
  resolveStagedEligibilities,
  sampleEligibilities,
  type PositionDraft,
} from '../../positions/utils/positionDraft';
import { samplePosition } from '../../positions/utils/samplePosition';
import type { OrgUnitOption } from '../../organization/organizationTypes';
import { plantillaApi, salaryGradesApi } from '../plantillaApi';
import { samplePlantillaPost } from '../utils/samplePlantillaPost';
import {
  blankItemNumberRow,
  ITEM_NUMBERS_MAX,
  itemNumberRows,
  itemNumbersOf,
  itemNumbersValid,
  itemNumberSet,
  type ItemNumberRow,
} from '../utils/itemNumbers';
import { ItemNumberGrid } from './ItemNumberGrid';
import { GroupCard } from '../../../../components/GroupCard';
import { Tooltip } from '../../../../components/Tooltip';
import { useToast } from '../../../../components/toastContext';
import { uid } from '../../../../lib/uid';
import { Plus } from 'lucide-react';
import {
  SALARY_TYPE,
  salaryTypeLabel,
  type PlantillaPositionListItem,
  type SalaryGradeOption,
} from '../plantillaTypes';

/** Where the post's designation comes from — a title already in the catalog, or a new one. */
type PositionSource = 'existing' | 'new';

/** The new-position fields' id prefix; the server's `newPosition.<field>` errors map onto it. */
const NEW_POSITION_ID = 'plantilla-new-position';

/** One unit card of the create — a unit and the slots it authorizes there. */
interface UnitCard {
  key: string;
  unitId: string | null;
  rows: ItemNumberRow[];
}

/** The cap the server holds on units per create — stated where the reader meets it. */
const UNITS_MAX = 50;
const TOTAL_ITEMS_REASON = `One save may authorize at most ${ITEM_NUMBERS_MAX} items across all units.`;

/**
 * Where a CARD's refused field lives: `units[1].orgUnitId` and `units[1].itemNumbers[2]` name
 * the card by index, and so do its inputs. Built from the refusal's own keys, because a row is
 * named by index and no static map can list every one. A refusal of the whole list points at the
 * first card's unit.
 */
function unitFieldIds(refusedFields: string[]) {
  const ids: Record<string, string> = {};

  for (const field of refusedFields) {
    if (field === 'units') {
      ids[field] = 'plantilla-position-units-0-org-unit-id';
      continue;
    }

    const match = /^units[(d+)].(orgUnitId|itemNumbers)([d+])?$/.exec(field);
    if (!match) continue;

    const [, card, part, row] = match;
    ids[field] =
      part === 'orgUnitId'
        ? `plantilla-position-units-${card}-org-unit-id`
        : `plantilla-position-units-${card}-item-numbers${row ?? '[0]'}`;
  }

  return ids;
}

/**
 * Create or edit one authorized post.
 *
 * Two pickers and one read-only detail: the position comes from the catalog, the unit from
 * the chart, and the grade is DISPLAYED rather than chosen — a designation carries its grade,
 * so the server copies it from the chosen title and the form shows what that will be. The
 * amount beside it is read from the salary schedule, because a post is pegged to a grade
 * rather than to one tranche's rate.
 */
export function PlantillaPositionModal({
  open,
  editing,
  defaultUnitId,
  onClose,
  onSaved,
  onCreated,
}: {
  open: boolean;
  /** Null creates. */
  editing: PlantillaPositionListItem | null;
  /**
   * The unit the list is currently filtered to, if exactly one — the picker opens on it.
   *
   * A suggestion, not a lock: the reader narrowed the pattern to a unit and is now adding a
   * post, so that unit is the answer nine times in ten. They can still pick another, which is
   * why this is a seeded value rather than a disabled field.
   */
  defaultUnitId?: string | null;
  onClose: () => void;
  onSaved: () => void;
  /** The post just created, for the caller to open its profile. */
  onCreated?: (position: PlantillaPositionListItem) => void;
}) {
  /*
    Adding items to an existing plantilla is a headcount change, which carries
    `plantilla.manage-slots` rather than `plantilla.create` — two different privileges, and a
    caller can hold one without the other. Read here so the form states what will happen
    instead of promising an append the server will refuse.
  */
  const { hasPermission } = useAuth();
  const canAddItems = hasPermission('plantilla.manage-slots');
  const toast = useToast();

  /*
    Adding a title to the catalog is `positions.create`'s to do, and the server refuses a new
    position from anyone without it. So the choice is offered only to a reader who holds it —
    a pure permission gate HIDES — and only on a create: an edit re-points at an existing title.
  */
  const canCreatePosition = editing === null && hasPermission('positions.create');
  const [source, setSource] = useState<PositionSource>('existing');
  const creatingPosition = canCreatePosition && source === 'new';

  // The new title's fields, in the catalog form's own shape — see <PositionFields>.
  const [draft, setDraft] = useState<PositionDraft>(() => positionDraftFrom(null));
  const [eligibilities, setEligibilities] = useState<EligibilityOption[]>([]);

  // Seeded once, on mount. The parent mounts this only while it is open.
  const [positionId, setPositionId] = useState<string | null>(editing?.positionId ?? null);
  const [unitId, setUnitId] = useState<string | null>(editing?.orgUnitId ?? defaultUnitId ?? null);

  /*
    The post's own id, for the edit form to show. Never sent and never previewed on a create:
    the server assigns it inside the write.
  */
  const [code] = useState(editing?.code ?? '');

  /*
    THE ITEMS, ONE ROW EACH — product call, 09 Oct 2026, replacing a `Slots` number box.

    A headcount typed as a figure asks the reader to count what they are authorizing; the rows
    ARE the count, and each one shows the item number it will be given. Same grid every other
    add-many surface uses, so the plantilla stops being the one place a sub-record is a number.

    Each row carries the item number the reader TYPES (product call, 16 Oct 2026) — the
    official number off the plantilla, unique across every item there has ever been. See
    `<ItemNumberGrid>`.

    ONE CARD PER UNIT (19 Oct 2026): the designation is authorized in several units at once,
    each card a unit and its own slots. The first opens on the unit the list is filtered to.
  */
  const [cards, setCards] = useState<UnitCard[]>(() => [
    { key: uid(), unitId: defaultUnitId ?? null, rows: [blankItemNumberRow()] },
  ]);

  // Collapsed card keys — never expanded ones, so a new card opens open.
  const [collapsedCards, setCollapsedCards] = useState<Set<string>>(() => new Set());

  const [units, setUnits] = useState<OrgUnitOption[]>([]);
  const [grades, setGrades] = useState<SalaryGradeOption[]>([]);
  const [positions, setPositions] = useState<PositionOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  /*
    The posts this designation ALREADY has, by unit — a card whose unit is among them adds its
    slots to that post rather than a second one (a unit holds a designation once). Fetched once
    for the chosen title rather than per card; empty on the new-position path, where a title
    that does not exist yet is held nowhere.
  */
  const [existingByUnit, setExistingByUnit] = useState<Map<string, PlantillaPositionListItem>>(() => new Map());

  // The stepper's position: 0 the position, 1 the unit and slots.
  const [step, setStep] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    Promise.all([
      orgUnitsApi.options(undefined, controller.signal),
      salaryGradesApi.options(controller.signal),
      positionsApi.options(controller.signal),
      /*
        The eligibility catalog only for a reader who may add a title — it feeds the new
        position's picker and nothing else, and a reader without `positions.create` holds no
        implied read of it, so fetching it anyway would fail the whole load.
      */
      canCreatePosition
        ? eligibilitiesApi.options(controller.signal)
        : Promise.resolve<EligibilityOption[]>([]),
    ])
      .then(([loadedUnits, loadedGrades, loadedPositions, loadedEligibilities]) => {
        setUnits(loadedUnits);
        setGrades(loadedGrades);
        setPositions(loadedPositions);

        /*
          No position to choose from: the form opens on "Create new position" — for a reader
          who may add one — rather than on a picker with nothing in it.
        */
        if (loadedPositions.length === 0 && canCreatePosition) setSource('new');
        setEligibilities(loadedEligibilities);
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        const failure = caught instanceof ApiError ? caught : new ApiError('Could not load the pickers.', 0);

      // No focus call: a failed picker fetch names no field, so there is nothing to point at.
      setError(failure);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingOptions(false);
      });

    return () => controller.abort();
  }, [canCreatePosition]);

  /*
    Re-read whenever the title changes. The answer is a fact about the database, so it is
    fetched rather than derived from the list behind the modal — which may be filtered, paged,
    or minutes stale. A failed read leaves it EMPTY: the server makes the same decision inside
    the write, so the worst case is a banner not drawn, never a wrong save.
  */
  useEffect(() => {
    if (editing !== null || creatingPosition || !positionId) {
      setExistingByUnit(new Map());
      return;
    }

    const controller = new AbortController();

    plantillaApi
      .list({ positionId, status: 'active', pageSize: 200 }, controller.signal)
      .then((page) => setExistingByUnit(new Map(page.items.map((post) => [post.orgUnitId, post]))))
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setExistingByUnit(new Map());
      });

    return () => controller.abort();
  }, [editing, creatingPosition, positionId]);

  function patchCard(key: string, patch: Partial<UnitCard>) {
    setCards((current) => current.map((card) => (card.key === key ? { ...card, ...patch } : card)));
  }

  function addCard() {
    setCards((current) => [...current, { key: uid(), unitId: null, rows: [blankItemNumberRow()] }]);
  }

  function removeCard(key: string) {
    setCards((current) => (current.length > 1 ? current.filter((card) => card.key !== key) : current));
  }

  function toggleCard(key: string) {
    setCollapsedCards((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  /*
    A card is valid when its unit is chosen, every row is typed, and no number repeats — in it
    or in another card. One that would add to an existing post is NOT valid for a reader without
    manage-slots: the server would refuse the whole save, and saying so here is kinder.
  */
  const cardValid = (card: UnitCard) => {
    const elsewhere = new Set(
      cards.filter((other) => other.key !== card.key).flatMap((other) => [...itemNumberSet(other.rows)]),
    );

    return (
      card.unitId !== null &&
      itemNumbersValid(card.rows, elsewhere) &&
      (canAddItems || !existingByUnit.has(card.unitId))
    );
  };

  const slotsValid = cards.every(cardValid);

  // The server caps the WHOLE request at one post's ceiling, so the grids share it.
  const totalRows = cards.reduce((sum, card) => sum + card.rows.length, 0);

  const addCardReason =
    cards.length >= UNITS_MAX
      ? `A post may be authorized in at most ${UNITS_MAX} units at once.`
      : totalRows >= ITEM_NUMBERS_MAX
        ? TOTAL_ITEMS_REASON
      : cards.some((card) => card.unitId === null)
        ? 'Choose the unit in every card before adding another.'
        : null;

  /*
    One validity per step. Neither the id nor the order is a field any more, so neither is a
    condition; the Submit is drawn on every step but enables only when both hold.
  */
  const positionValid = creatingPosition ? positionDraftValid(draft) : Boolean(positionId);
  const unitValid = editing !== null ? Boolean(unitId) : slotsValid;

  const steps = [
    { key: 'position', label: 'Position', valid: positionValid },
    { key: 'unit', label: editing ? 'Unit' : 'Unit and slots', valid: unitValid },
  ] as const;

  const isLastStep = step === steps.length - 1;
  const canSave = positionValid && unitValid && confirmed && !saving;

  /*
    The grade this post carries, and it is NOT a field.

    A designation carries its grade — the catalog is where that is decided — so the form shows
    what the chosen title brings rather than asking again. It reads from the CHOSEN TITLE on
    both paths, which is also where the list reads it: attaching or changing a grade in the
    catalog reaches every post drawn from it at once, so a post and its designation can never
    disagree about what it is authorized at.
  */
  const chosen = positionId === null ? null : positions.find((p) => p.id === positionId);

  // On the new-position path the title is the draft, so its grade is the one being picked.
  const drafted = creatingPosition ? positionDraftGrade(draft) : null;

  const grade = creatingPosition
    ? {
        salaryGrade: drafted?.salaryGrade ?? null,
        salaryGradeType: drafted?.salaryGradeType ?? null,
        salaryStep: drafted?.salaryStep ?? null,
      }
    : {
        salaryGrade: chosen?.salaryGrade ?? null,
        salaryGradeType: chosen?.salaryGradeType ?? null,
        salaryStep: chosen?.salaryStep ?? null,
      };

  /*
    What that grade and step currently pay, from the schedule the form already loaded. Read
    here rather than stored on the post: a post is pegged to a grade, and a stored amount
    would go stale the moment a new tranche is published.
  */
  const rate =
    grade.salaryGrade == null
      ? null
      : (grades.find(
          (g) =>
            g.grade === grade.salaryGrade &&
            g.step === (grade.salaryStep ?? 1) &&
            g.type === (grade.salaryGradeType ?? 0),
        )?.currentMonthlyAmount ?? null);

  /**
   * Fills every required field so the primary action enables on the first click.
   *
   * The two foreign keys are picked from the options already loaded rather than invented —
   * an id the generator made up fails the server's "it must exist and be active" check, which
   * reads as a bug in the form. The grade needs nothing here: it follows whichever title is
   * picked.
   */
  function autogenerate() {
    // The id is not generated here: it is the server's, and the preview follows the title.
    const sample = samplePlantillaPost();

    // ONE card, a random live unit and the sample's numbers — the floor; a reader adds more.
    setCards([
      {
        key: uid(),
        unitId: units.length > 0 ? units[Math.floor(Math.random() * units.length)].id : null,
        rows: itemNumberRows(sample.itemNumbers),
      },
    ]);

    if (creatingPosition) {
      /*
        The new title's required fields, the grade and eligibility resolved against the live
        catalogs rather than invented, and a row or two of each standard, exactly as on the
        Positions form.
      */
      const title = samplePosition();
      const gradeOptions = gradeOptionsFor(grades);

      setDraft((current) => ({
        ...current,
        title: title.title,
        code: title.code,
        otherExpectations: title.otherExpectations,
        ...qualificationRowsFrom(title),
        ...sampleEligibilities(eligibilities),
        ...(gradeOptions.length > 0
          ? { gradeKey: gradeOptions[Math.floor(Math.random() * gradeOptions.length)].value }
          : {}),
      }));
    } else if (positions.length > 0) {
      setPositionId(positions[Math.floor(Math.random() * positions.length)]!.id);
    }

  }

  /**
   * Writes one staged eligibility under the catalog's own key, and keeps it in the picker's
   * options so a refused save still shows the row it now names.
   */
  async function writeEligibility(name: string) {
    const created = await eligibilitiesApi.create({ name, description: null, entailsLicense: false });
    setEligibilities((current) => [...current, { id: created.id, name: created.name, entailsLicense: false }]);
    return created.id;
  }

  /** The cards as the request's units — only ever built once every card is valid. */
  const unitsOf = () =>
    cards.map((card) => ({ orgUnitId: card.unitId ?? '', itemNumbers: itemNumbersOf(card.rows) }));

  /**
   * What a create answered with: ONE post opens its profile, as it always did; SEVERAL return the
   * reader to the list with what was made (decision B, 19 Oct 2026).
   */
  function reportCreated(posts: PlantillaPositionListItem[]) {
    if (posts.length === 1) {
      onCreated?.(posts[0]);
      return;
    }

    toast.success(`${posts.length} plantilla posts saved — one per unit.`);
  }

  async function save() {
    if (!canSave || (!creatingPosition && !positionId)) return;

    setSaving(true);
    setError(null);

    try {
      if (creatingPosition) {
        /*
          The title and the post in ONE request, so they commit together: a post refused after
          its title had been saved would leave a catalog row nobody asked for, and a retry
          would collide with it.
        */
        // Eligibilities typed into a picker are written NOW, at save — see resolveStagedEligibilities.
        const resolved = await resolveStagedEligibilities(draft, writeEligibility);
        setDraft(resolved);

        reportCreated(
          await plantillaApi.create({ newPosition: positionRequestFrom(resolved), units: unitsOf() }),
        );
      } else if (!positionId) {
        return;
      } else if (editing) {
        if (!unitId) return;
        await plantillaApi.update(editing.id, { positionId, orgUnitId: unitId });
      } else {
        /*
          EVERY CARD IN ONE REQUEST: a new post per unit, or the slots ADDED to the post a unit
          already has (the server decides, inside the write, and refuses it without
          manage-slots). All saved or none.
        */
        reportCreated(await plantillaApi.create({ positionId, units: unitsOf() }));
      }

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the position.', 0);

      setError(failure);

      // Say which field, show it, and — on a stepped form — go to its step.
      // A collapsed card's body is unmounted — open every card first, or the field is not there.
      setCollapsedCards(new Set());

      focusValidationError(failure, {
        idPrefix: 'plantilla-position',
        /*
          The fields whose input is not named after them. Without these the helper resolves
          nothing and the reader is told which field but never shown it — the case the
          development warning in `focusValidationError` exists to catch.
        */
        fieldIds: {
          positionId: 'plantilla-position-position',
          orgUnitId: 'plantilla-position-unit',
          // A card's fields are named by its index — `units[1].itemNumbers[2]` — built from the refusal.
          ...unitFieldIds(Object.keys(failure.fields)),
          // The new title's fields, named by the server under `newPosition.` — a refused ROW
          // is named by index, so that half of the map is built from the refusal itself.
          ...positionFieldIds(NEW_POSITION_ID, Object.keys(failure.fields), 'newPosition.'),
        },
        // The position step holds the designation and every new-title field; the unit step the rest.
        stepOf: (field) => (field === 'positionId' || field.startsWith('newPosition.') ? 0 : 1),
        onStep: setStep,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit plantilla' : 'New plantilla'}
      /*
        Two-column rows and a unit picker whose labels carry the unit name and its tier, so
        `md` clipped both. Matches OrgUnitModal, the other create form in this suite.
      */
      size="2xl"
      id="plantilla-position-modal"
      // Create only. On a form over existing data it would overwrite the record's real values.
      autogenerate={editing === null ? { onGenerate: autogenerate, disabled: saving } : undefined}
      // The last step only: the strip appearing IS the signal that nothing is left to fill in.
      confirm={
        isLastStep
          ? { id: 'plantilla-position-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }
          : undefined
      }
      footer={
        <>
          <Button id="plantilla-position-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          {step > 0 ? (
            <Button
              id="plantilla-position-back"
              variant="secondary"
              onClick={() => setStep((current) => Math.max(0, current - 1))}
              disabled={saving}
            >
              Back
            </Button>
          ) : null}
          {!isLastStep ? (
            <Button
              id="plantilla-position-next"
              variant="secondary"
              onClick={() => setStep((current) => Math.min(steps.length - 1, current + 1))}
              disabled={!steps[step].valid}
            >
              Next
            </Button>
          ) : null}
          <Button
            id="plantilla-position-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          Two steps (product call, 16 Oct 2026): the POSITION first — it is the question with
          the most to answer, and a new title is a form of its own — then where the post sits
          and how many items it authorizes.
        */}
        <Stepper
          id="plantilla-position"
          steps={steps.map(({ key, label }) => ({ key, label }))}
          activeIndex={step}
          onJump={(index) => {
            if (index <= step || steps.slice(0, index).every((s) => s.valid)) setStep(index);
          }}
        />

        <div
          id={`plantilla-position-panel-${steps[step].key}`}
          role="tabpanel"
          aria-labelledby={`plantilla-position-step-${steps[step].key}`}
          className="flex flex-col gap-4"
        >
        {steps[step].key === 'position' ? (
          <>
        {/*
          Where the designation comes from. Drawn only for a reader who may add a title — for
          anyone else there is one answer, and a choice with one option is not a choice.
        */}
        {canCreatePosition ? (
          <RadioGroup<PositionSource>
            id="plantilla-position-source"
            label="Position"
            required
            value={source}
            onChange={setSource}
            options={[
              {
                value: 'existing',
                label: 'Select existing position',
                description: 'Draw this post from a title already in the catalog.',
              },
              {
                value: 'new',
                label: 'Create new position',
                description: 'Add a title to the catalog and draw this post from it, in one save.',
              },
            ]}
            disabled={saving}
          />
        ) : null}

        {/*
          A NEW title: the catalog form's own fields, so a title added here is asked exactly
          what the Positions form asks. Saved with the post, in one request.
        */}
        {creatingPosition ? (
          <PositionFields
            idPrefix={NEW_POSITION_ID}
            draft={draft}
            onChange={(next) => setDraft((current) => ({ ...current, ...next }))}
            grades={grades}
            eligibilities={eligibilities}
            loadingOptions={loadingOptions}
            disabled={saving}
          />
        ) : null}

        {/*
          TWO COLUMNS ONLY WHERE THERE ARE TWO FIELDS. The post's id sits beside the
          designation on an edit; on a create the slots are their own grid below, so a
          half-width position picker would leave the row half empty under a full-width unit.
        */}
        {creatingPosition ? null : (
        <div className={editing ? 'grid grid-cols-1 gap-4 sm:grid-cols-2' : ''}>
          {/*
            The first question now (the position step), so it waits on nothing.
          */}
          <Select
            id="plantilla-position-position"
            label="Position"
            required
            remote
            searchable
            loading={loadingOptions}
            description="The catalog title this post is drawn from. Managed under Organization → Settings → Positions; its qualification standards apply to this post."
            value={positionId}
            onChange={setPositionId}
            options={positions.map((p) => ({ value: p.id, label: p.title, right: p.code }))}
            error={positionId ? undefined : 'Choose the position this post is drawn from.'}
            disabled={saving}
          />

          {/*
            On an EDIT the post's own id sits beside the designation — it is the same fact
            continued, and the items are added from the profile rather than here. On a create
            the slots are a GRID below instead, so the position takes the row alone.
          */}
          {editing ? (
            <TextField
              id="plantilla-position-code"
              label="Plantilla ID"
              description="Permanent — it numbers this post's items, and those numbers are never reissued."
              value={code}
              onChange={() => undefined}
              readOnly
              disabled
            />
          ) : null}
        </div>
        )}

        {/*
          The grade, shown rather than asked for.

          A label/value table because none of it is a decision made here: the designation
          carries the grade and the step, and the schedule decides what they pay. A picker
          would be asking a question the catalog has already answered, and a second copy of
          the answer to keep in step with it.

          Not on the new-position path (16 Oct 2026): there the grade IS asked, and the new
          title's own picker shows each grade's type and amount in its options.
        */}
        {creatingPosition ? null : (
        <div>
          <p className="pb-1.5 text-sm text-slate-600">Salary grade</p>

          <PreviewTable
            /*
              The Table Display View — the tinted, uppercase label column.

              Product call for this form: both tables here are read-only FACTS rather
              than fields, and in a stack of them the tint is what separates a key from
              a value at a glance. Recorded in CLAUDE.md, which previously scoped this
              variant to profile cards.
            */
            variant="table-display"
            rows={[
              {
                label: 'SG',
                value:
                  grade.salaryGrade == null ? null : (
                    <span className="font-mono tabular-nums">
                      {grade.salaryGrade}
                      {grade.salaryGradeType === SALARY_TYPE.health
                        ? ` (${salaryTypeLabel(grade.salaryGradeType)})`
                        : ''}
                    </span>
                  ),
              },
              {
                label: 'Step',
                value:
                  grade.salaryStep == null ? null : (
                    <span className="font-mono tabular-nums">{grade.salaryStep}</span>
                  ),
              },
              {
                label: 'Amount',
                value:
                  rate == null ? null : (
                    <span className="font-mono tabular-nums">
                      {rate.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  ),
              },
            ]}
          />
        </div>
        )}
          </>
        ) : (
          <>
        {editing ? (
          /*
            AN EDIT IS ONE POST, so one unit — its items are managed from the profile.
          */
          <Select
            id="plantilla-position-unit"
            label="Unit"
            required
            remote
            searchable
            loading={loadingOptions}
            description="The unit this post sits in. Managed under Organization."
            value={unitId}
            onChange={setUnitId}
            options={units.map((u) => ({ value: u.id, label: u.name, right: u.orgUnitTypeName }))}
            error={unitId ? undefined : 'Choose the unit this position sits in.'}
            disabled={saving}
          />
        ) : (
          <>
            {/*
              A STACK OF UNIT CARDS (19 Oct 2026) — the purchase order's supplier stack, one shared
              `<GroupCard>`: each unit becomes its own post of the designation, with its own typed
              slots. A unit chosen in one card is not offered in another — two cards for one unit
              would be one post with its items split.
            */}
            {cards.map((card, index) => {
              const takenUnits = new Set(cards.filter((other) => other.key !== card.key).map((other) => other.unitId));
              const unitName = units.find((u) => u.id === card.unitId)?.name ?? null;
              const existing = card.unitId === null ? undefined : existingByUnit.get(card.unitId);
              const elsewhere = new Set(
                cards.filter((other) => other.key !== card.key).flatMap((other) => [...itemNumberSet(other.rows)]),
              );
              const count = card.rows.filter((row) => row.itemNumber.trim().length > 0).length;

              return (
                <GroupCard
                  key={card.key}
                  id={`plantilla-position-units-${index}`}
                  title={`Plantilla ${index + 1}`}
                  subjectLabel="Unit"
                  subject={unitName}
                  collapsed={collapsedCards.has(card.key)}
                  onToggle={() => toggleCard(card.key)}
                  summary={count > 0 ? ` · ${count} slot${count === 1 ? '' : 's'}` : null}
                  onRemove={cards.length > 1 ? () => removeCard(card.key) : undefined}
                  removeDisabled={saving}
                >
                  <Select
                    id={`plantilla-position-units-${index}-org-unit-id`}
                    label="Unit"
                    required
                    remote
                    searchable
                    loading={loadingOptions}
                    description="The unit this post sits in. Managed under Organization."
                    value={card.unitId}
                    onChange={(next) => patchCard(card.key, { unitId: next })}
                    options={units
                      .filter((u) => !takenUnits.has(u.id))
                      .map((u) => ({ value: u.id, label: u.name, right: u.orgUnitTypeName }))}
                    error={card.unitId ? undefined : 'Choose the unit this position sits in.'}
                    disabled={saving}
                  />

                  <ItemNumberGrid
                    idPrefix={`plantilla-position-units-${index}`}
                    rows={card.rows}
                    onRows={(rows) => patchCard(card.key, { rows })}
                    elsewhere={elsewhere}
                    max={ITEM_NUMBERS_MAX - (totalRows - card.rows.length)}
                    maxReason={TOTAL_ITEMS_REASON}
                    disabled={saving}
                  />

                  {/*
                    What this unit already has for this designation, and what the save will do:
                    the slots JOIN that post — a unit holds a designation once. Shown rather than
                    refused; it is a headcount change, so it needs manage-slots, and says so.
                  */}
                  {existing ? (
                    <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3">
                      <p className="text-xs text-slate-700">
                        <span className="font-medium">{existing.title}</span> is already authorized in
                        this unit as <span className="font-mono">{existing.code}</span> —{' '}
                        <span className="font-mono tabular-nums">{existing.authorizedSlots}</span> item
                        {existing.authorizedSlots === 1 ? '' : 's'},{' '}
                        <span className="font-mono tabular-nums">{existing.vacantSlots}</span> vacant.{' '}
                        {canAddItems
                          ? 'The items you add here join it — no second post is created.'
                          : 'Adding items to it needs the manage-slots permission, which this account does not hold — remove this unit or the save will be refused.'}
                      </p>
                    </div>
                  ) : null}
                </GroupCard>
              );
            })}

            <div>
              {/* The reason goes in a `<Tooltip disabledReason>` — the purchase order's pattern. */}
              <Tooltip disabledReason={addCardReason}>
                <Button
                  id="plantilla-position-add-unit"
                  variant="secondary"
                  onClick={addCard}
                  disabled={saving || addCardReason !== null}
                  leading={<Plus className="h-4 w-4" aria-hidden="true" />}
                >
                  Add another unit
                </Button>
              </Tooltip>
            </div>
          </>
        )}
          </>
        )}
        </div>
      </div>
    </Modal>
  );
}
